/**
 * Unified chat push notification click handling (web + native).
 * Cloud Function payloads include data.booking_id for chat_message notifications.
 */

import { Capacitor } from '@capacitor/core';

type NavigateFn = (path: string) => void;

let navigateFn: NavigateFn | null = null;
let pendingBookingId: string | null = null;

let webClickHandler:
  | ((event: { notification?: { additionalData?: Record<string, unknown> } }) => void)
  | null = null;
let webListenerAttached = false;

let nativeClickHandler: ((event: unknown) => void) | null = null;
let nativeListenerAttached = false;

export function extractBookingIdFromNotificationPayload(
  payload: unknown,
): string | null {
  if (!payload || typeof payload !== 'object') return null;

  const data = payload as Record<string, unknown>;
  const raw = data.booking_id ?? data.bookingId;
  if (typeof raw !== 'string') return null;

  const bookingId = raw.trim();
  return bookingId || null;
}

function navigateToChat(bookingId: string): void {
  const path = `/jobs/chat/${encodeURIComponent(bookingId)}`;
  if (navigateFn) {
    navigateFn(path);
    return;
  }
  pendingBookingId = bookingId;
}

export function handleChatNotificationPayload(payload: unknown): void {
  const bookingId = extractBookingIdFromNotificationPayload(payload);
  if (!bookingId) return;

  const type =
    payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>).type
      : undefined;
  if (type != null && type !== 'chat_message') return;

  navigateToChat(bookingId);
}

export function setChatNotificationNavigator(navigate: NavigateFn): void {
  navigateFn = navigate;

  if (pendingBookingId) {
    navigateToChat(pendingBookingId);
    pendingBookingId = null;
  }
}

export function clearChatNotificationNavigator(): void {
  navigateFn = null;
}

async function attachWebChatNotificationClickListener(): Promise<void> {
  if (webListenerAttached || typeof window === 'undefined') return;

  try {
    const { default: OneSignal } = await import('react-onesignal');
    webClickHandler = (event) => {
      handleChatNotificationPayload(event.notification?.additionalData);
    };
    OneSignal.Notifications.addEventListener('click', webClickHandler);
    webListenerAttached = true;
  } catch {
    /* web SDK unavailable (native build) */
  }
}

async function detachWebChatNotificationClickListener(): Promise<void> {
  if (!webListenerAttached || !webClickHandler) return;

  try {
    const { default: OneSignal } = await import('react-onesignal');
    OneSignal.Notifications.removeEventListener('click', webClickHandler);
  } catch {
    /* ignore */
  }

  webClickHandler = null;
  webListenerAttached = false;
}

async function attachNativeChatNotificationClickListener(): Promise<void> {
  if (nativeListenerAttached || !Capacitor.isNativePlatform()) return;

  try {
    const { default: OneSignal } = await import('@onesignal/capacitor-plugin');
    nativeClickHandler = (event: unknown) => {
      const notification = (event as { notification?: { additionalData?: unknown } })
        ?.notification;
      handleChatNotificationPayload(notification?.additionalData);
    };
    OneSignal.Notifications.addEventListener('click', nativeClickHandler);
    nativeListenerAttached = true;
  } catch (error) {
    console.warn('[OneSignal] Failed to attach native chat click listener:', error);
  }
}

async function detachNativeChatNotificationClickListener(): Promise<void> {
  if (!nativeListenerAttached || !nativeClickHandler) return;

  try {
    const { default: OneSignal } = await import('@onesignal/capacitor-plugin');
    OneSignal.Notifications.removeEventListener('click', nativeClickHandler);
  } catch {
    /* ignore */
  }

  nativeClickHandler = null;
  nativeListenerAttached = false;
}

/** Attach click listeners early (before React Router) so cold-start taps are retained. */
export async function initializeChatNotificationClickListeners(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await attachNativeChatNotificationClickListener();
  } else {
    await attachWebChatNotificationClickListener();
  }
}

/** Register web and/or native notification click handlers for chat deep links. */
export async function setupChatNotificationHandlers(
  navigate: NavigateFn,
): Promise<void> {
  setChatNotificationNavigator(navigate);
  await initializeChatNotificationClickListeners();
}

export async function teardownChatNotificationHandlers(): Promise<void> {
  clearChatNotificationNavigator();
  await Promise.all([
    detachWebChatNotificationClickListener(),
    detachNativeChatNotificationClickListener(),
  ]);
}
